// utils/avatarHelpers.ts — Avatar color generation and initials utilities

/**
 * A curated "Vert & Or" (emerald + antique gold, heraldic-adjacent) palette
 * for avatar background colors. These complement the Folk Of Interest design system.
 */
const AVATAR_PALETTE = [
  '#A07A3F', // Antique gold (primary)
  '#3E6FA8', // Paternal blue (secondary)
  '#F0D9A0', // Gold highlight (accent)
  '#569578', // Emerald / sage
  '#7d5f2e', // Deep gold
  '#001C4C', // Deep navy
  '#D9B978', // Warm gold
  '#275d46', // Forest emerald
  '#6D96C6', // Light paternal blue
  '#B4534B', // Maternal red
  '#6A0302', // Deep burgundy
  '#8A6A34', // Bronze
  '#2C5384', // Slate blue
  '#101c13', // Deep forest
  '#C9915B', // Muted amber
  '#4E564C', // Moss grey
];

/**
 * Compute a deterministic integer hash from a string.
 * Uses djb2 algorithm for good distribution.
 */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) + hash + char; // hash * 33 + char
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get a deterministic avatar background color for a given name.
 * The same name always produces the same color.
 */
export function getAvatarColor(name: string): string {
  if (!name || name.trim().length === 0) return AVATAR_PALETTE[0]!;
  const hash = hashString(name.trim().toLowerCase());
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

/**
 * Get a text color (light or dark) that contrasts well with the given background.
 * Uses WCAG relative luminance formula.
 */
export function getAvatarTextColor(backgroundColor: string): string {
  // Parse hex color
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Compute relative luminance (WCAG 2.1)
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  // Use white text on dark backgrounds, dark text on light backgrounds
  return luminance > 0.35 ? '#221c17' : '#fafaf8';
}

/**
 * Generate initials from a display name.
 * - "Ramesh Sharma" → "RS"
 * - "Priya" → "P"
 * - "Dr. A. B. Chandra" → "AC" (skips single-letter particles)
 * - Supports Devanagari and other Unicode scripts
 */
export function getInitials(displayName: string, maxLength = 2): string {
  if (!displayName || displayName.trim().length === 0) return '?';

  // Split by whitespace and filter out empty parts
  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);

  if (parts.length === 0) return '?';

  if (parts.length === 1) {
    // Single word — take first 1-2 characters
    const word = parts[0]!;
    return Array.from(word).slice(0, maxLength).join('').toUpperCase();
  }

  // Multi-word: take first char of first meaningful word + first char of last word
  // Skip prefixes like "Dr.", "Mr.", "Mrs.", "Prof."
  const prefixes = new Set(['dr', 'mr', 'mrs', 'ms', 'prof', 'shri', 'smt', 'km']);
  const meaningful = parts.filter((p) => !prefixes.has(p.toLowerCase().replace(/\.$/, '')));

  if (meaningful.length === 0) {
    return Array.from(parts[0]!).slice(0, maxLength).join('').toUpperCase();
  }

  if (meaningful.length === 1) {
    return Array.from(meaningful[0]!).slice(0, maxLength).join('').toUpperCase();
  }

  // First char of first word + first char of last word
  const firstChar = Array.from(meaningful[0]!)[0] ?? '';
  const lastChar = Array.from(meaningful[meaningful.length - 1]!)[0] ?? '';

  return (firstChar + lastChar).toUpperCase();
}

/**
 * Get full avatar info (color + initials) for a node.
 * Returns a stable, deterministic set of values.
 */
export function getAvatarInfo(displayName: string): {
  initials: string;
  bgColor: string;
  textColor: string;
} {
  const bgColor = getAvatarColor(displayName);
  return {
    initials: getInitials(displayName),
    bgColor,
    textColor: getAvatarTextColor(bgColor),
  };
}

/**
 * Get a ghost/silhouette color (muted, greyed variant) for unclaimed nodes.
 */
export function getGhostAvatarColor(): { bgColor: string; textColor: string } {
  return {
    bgColor: '#4E564C', // --color-neutral-400 (Vert & Or)
    textColor: '#C9C7BC', // --color-neutral-100 (Vert & Or)
  };
}

/**
 * Get a deceased node avatar color (desaturated, grey).
 */
export function getDeceasedAvatarColor(): { bgColor: string; textColor: string } {
  return {
    bgColor: '#6E7568', // --color-neutral-300 (Vert & Or)
    textColor: '#EDE6D6', // light ink
  };
}

/**
 * Generate a CSS filter string to desaturate an avatar for deceased nodes.
 */
export const DECEASED_AVATAR_FILTER = 'grayscale(80%) opacity(0.85)';
